import schoolLogo from '@/assets/mfu_logo.png'
import { downloadAgreementPdf } from '@/utils/agreement-download'
import { buildAgreementDocxBlob, buildDocxBlob } from '@/utils/word-export'

const TRANSLATED_META_LINE_PATTERN = /^(agreement\s+(title|type|articles)|document\s+(title|type)|partner\s+institution|country|target\s+language|prepared\s+for|ชื่อข้อตกลง|ประเภทข้อตกลง|สถาบันพันธมิตร|ประเทศ|บทความข้อตกลง)\s*[:：]/i
const TRANSLATED_SIGNATURE_HEADING_PATTERN = /^(signature\s+block|signatories|ส่วนลงนาม|บล็อกลายเซ็น|ผู้ลงนาม)\s*[:：]?$/i
const TRANSLATED_SECTION_TITLE_PATTERN = /^(purpose|exchange\s+conditions|academic\s+record\s+and\s+credit\s+transfer|student\s+status\s+and\s+proficiency|effective\s+period|termination|additional\s+condition|language|student\s+mobility|financial\s+responsibilities|data\s+protection\s+and\s+privacy|cooperation\s+objectives)$/i
const TRANSLATED_COVER_CONNECTOR_PATTERN = /^(between|and|ระหว่าง|และ|と|및|和|与|et|y|und)$/i
const TRANSLATED_COVER_TITLE_PATTERN = /(agreement|memorandum|student|exchange|double\s+degree|extension|ข้อตกลง|บันทึก|นักศึกษา|แลกเปลี่ยน|協定|覚書|交換|学生|协议|备忘录|협정|교환|학생)/i
const TRANSLATED_COVER_INSTITUTION_PATTERN = /(mae\s+fah\s+luang|university|college|institution|institute|มหาวิทยาลัย|วิทยาลัย|สถาบัน|大学|대학교|université|universidad)/i
const TRANSLATED_COVER_META_PATTERN = /^(document\s+code|approved\s+version|prepared\s+for|รหัสเอกสาร|เวอร์ชัน|จัดเตรียมสำหรับ)\s*[:：]/i

function escapeHtml(value) {
  return String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;')
}

export function safeDocumentFileBase(value, fallback = 'document') {
  return String(value || fallback)
    .trim()
    .replace(/[^a-z0-9-_]+/gi, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .toLowerCase() || fallback
}

export function triggerDocumentDownload(href, fileName) {
  if (!href || typeof document === 'undefined') return false
  const link = document.createElement('a')
  link.href = href
  link.download = fileName || ''
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  return true
}

function normalizedExportLines(value) {
  return String(value || '')
    .replace(/\r\n/g, '\n')
    .split('\n')
    .map((line) => line.trim())
}

function withoutLeadingNumber(value) {
  return String(value || '')
    .replace(/^\s*(?:article|section)\s+\d+\s*[:.)-]?\s*/i, '')
    .replace(/^\s*\d+\s*[.)]\s*/, '')
    .trim()
}

function isLikelyTranslatedHeadingLine(line) {
  const normalized = String(line || '').trim()
  if (!normalized) return false
  if (TRANSLATED_META_LINE_PATTERN.test(normalized)) return true
  if (TRANSLATED_SIGNATURE_HEADING_PATTERN.test(normalized)) return true

  const withoutNumber = withoutLeadingNumber(normalized)
  if (!withoutNumber) return true
  if (TRANSLATED_SECTION_TITLE_PATTERN.test(withoutNumber)) return true

  const hasNumberPrefix = withoutNumber !== normalized
  const wordCount = withoutNumber.split(/\s+/).filter(Boolean).length
  const hasSentencePunctuation = /[.:;!?]$/.test(withoutNumber)
  return hasNumberPrefix && wordCount <= 12 && !hasSentencePunctuation
}

function compactBlankLines(lines) {
  const output = []
  lines.forEach((line) => {
    const value = String(line || '').trim()
    if (!value && (!output.length || !output[output.length - 1])) return
    output.push(value)
  })
  while (output.length && !output[0]) output.shift()
  while (output.length && !output[output.length - 1]) output.pop()
  return output
}

function splitLeadingTranslatedCoverTitle(lines) {
  const source = Array.isArray(lines) ? lines : []
  const start = source.findIndex((line) => String(line || '').trim())
  if (start < 0) {
    return { coverTitleLines: [], bodyLines: source }
  }

  const titleLines = []
  let lastTitleIndex = start - 1
  for (let index = start; index < source.length && titleLines.length < 8; index += 1) {
    const line = String(source[index] || '').trim()
    if (!line) {
      if (titleLines.length) break
      continue
    }
    if (titleLines.length && TRANSLATED_COVER_META_PATTERN.test(line)) break
    if (titleLines.length >= 3 && isLikelyTranslatedHeadingLine(line) && !TRANSLATED_COVER_CONNECTOR_PATTERN.test(line) && !TRANSLATED_COVER_INSTITUTION_PATTERN.test(line)) break
    titleLines.push(line)
    lastTitleIndex = index
    if (!String(source[index + 1] || '').trim()) break
  }

  const hasTitleLine = titleLines.some((line) => TRANSLATED_COVER_TITLE_PATTERN.test(line))
  const hasConnector = titleLines.some((line) => TRANSLATED_COVER_CONNECTOR_PATTERN.test(line))
  const institutionCount = titleLines.filter((line) => TRANSLATED_COVER_INSTITUTION_PATTERN.test(line)).length
  const looksLikeCoverTitle = titleLines.length >= 3 && (hasTitleLine || hasConnector || institutionCount >= 2)
  if (!looksLikeCoverTitle) {
    return { coverTitleLines: [], bodyLines: source }
  }

  let bodyStart = lastTitleIndex + 1
  while (bodyStart < source.length && !String(source[bodyStart] || '').trim()) bodyStart += 1

  return {
    coverTitleLines: titleLines,
    bodyLines: source.slice(0, start).concat(source.slice(bodyStart))
  }
}

function defaultSignatureText({ partner, country }) {
  const rightHeader = [partner, country].filter(Boolean).join(', ') || 'Partner Institution'
  return [
    `Mae Fah Luang University || ${rightHeader}`,
    '(Name) ........................................ || (Name) ........................................',
    '(Position) ........................................ || (Position) ........................................',
    'Date: || Date:'
  ].join('\n')
}

function splitTranslatedAgreementText(bodyText, options = {}) {
  const lines = normalizedExportLines(bodyText)
  const firstSignatureLine = lines.findIndex((line) => TRANSLATED_SIGNATURE_HEADING_PATTERN.test(line))
  const firstTwoColumnSignatureLine = lines.findIndex((line, index) => {
    if (!String(line || '').includes('||')) return false
    const remainingTwoColumnLines = lines.slice(index).filter((item) => String(item || '').includes('||')).length
    return remainingTwoColumnLines >= 2
  })
  const signatureStart = firstSignatureLine >= 0
    ? firstSignatureLine
    : firstTwoColumnSignatureLine

  const rawBodyLines = signatureStart >= 0 ? lines.slice(0, signatureStart) : lines
  const rawSignatureLines = signatureStart >= 0 ? lines.slice(signatureStart) : []
  const coverTitle = splitLeadingTranslatedCoverTitle(rawBodyLines)
  const bodyLines = compactBlankLines(coverTitle.bodyLines.filter((line) => !isLikelyTranslatedHeadingLine(line)))
  const signatureLines = compactBlankLines(rawSignatureLines.filter((line) => {
    const normalized = String(line || '').trim()
    return normalized && !TRANSLATED_SIGNATURE_HEADING_PATTERN.test(normalized)
  }))

  return {
    bodyText: bodyLines.join('\n'),
    coverTitleLines: coverTitle.coverTitleLines,
    signatureText: signatureLines.length ? signatureLines.join('\n') : defaultSignatureText(options)
  }
}

export async function downloadTextDocx({
  title,
  bodyText,
  fileBase,
  activityType,
  partner,
  country,
  partnerLogo,
  agreementStyle = true
}) {
  if (typeof URL === 'undefined' || typeof document === 'undefined') return false
  const translatedParts = agreementStyle
    ? splitTranslatedAgreementText(bodyText || '', { partner, country })
    : null
  const blob = agreementStyle
    ? await buildAgreementDocxBlob(title || 'Document', [
      {
        key: 'translated-body',
        title: 'Translated Content',
        content: translatedParts.bodyText || ''
      },
      {
        key: 'signature-block',
        title: 'Signature Block',
        content: translatedParts.signatureText || defaultSignatureText({ partner, country })
      }
    ], {
      activityType: activityType || '',
      partner: partner || '',
      country: country || '',
      includeDocumentTitle: false,
      includeSectionTitles: false,
      includeMfuLogo: true,
      mfuLogo: schoolLogo,
      partnerLogo: partnerLogo || null,
      coverTitleLines: translatedParts.coverTitleLines,
      includeCoverTitle: translatedParts.coverTitleLines.length > 0
    })
    : buildDocxBlob(title || 'Document', bodyText || '')
  const url = URL.createObjectURL(blob)
  const didDownload = triggerDocumentDownload(url, `${safeDocumentFileBase(fileBase || title)}.docx`)
  const timeout = typeof window !== 'undefined' && window.setTimeout ? window.setTimeout : setTimeout
  timeout(() => URL.revokeObjectURL(url), 1000)
  return didDownload
}

export function downloadTextPdf({
  title,
  bodyText,
  subtitle,
  activityType,
  partner,
  country,
  partnerLogo,
  agreementStyle = true
}) {
  if (typeof window === 'undefined') return false
  if (agreementStyle) {
    const translatedParts = splitTranslatedAgreementText(bodyText || '', { partner, country })

    return downloadAgreementPdf({
      title: title || 'Document',
      activityType: activityType || '',
      partner: partner || '',
      country: country || '',
      partnerLogo: partnerLogo || null,
      // Ensure translated header/body is treated as normal centered body
      // (prevents side-by-side rendering issues for Thai translated headings)
      type: 'translated'
    }, [
      {
        key: 'translated-body',
        title: 'Translated Content',
        content: translatedParts.bodyText || ''
      },
      {
        key: 'signature-block',
        title: 'Signature Block',
        content: translatedParts.signatureText || defaultSignatureText({ partner, country })
      }
    ], {
      coverTitleLines: translatedParts.coverTitleLines,
      includeCoverTitle: translatedParts.coverTitleLines.length > 0
    })
  }

  const popup = window.open('', '_blank')
  if (!(popup && popup.document)) return false
  const lines = String(bodyText || '')
    .split(/\r?\n/)
    .map((line) => `<p>${escapeHtml(line || ' ')}</p>`)
    .join('')

  try {
    popup.document.write(`<!doctype html>
<html>
<head>
  <meta charset="utf-8">
  <title>${escapeHtml(title || 'Document')}</title>
  <style>
    body { color: #111827; font-family: "Times New Roman", Times, serif; margin: 40px; }
    h1 { font-size: 22pt; margin: 0 0 8px; text-align: center; }
    .subtitle { color: #4b5563; font-size: 11pt; margin: 0 0 24px; text-align: center; }
    p { font-size: 12pt; line-height: 1.5; margin: 0 0 8px; white-space: pre-wrap; }
    @media print { body { margin: 24mm; } }
  </style>
</head>
<body>
  <h1>${escapeHtml(title || 'Document')}</h1>
  ${subtitle ? `<div class="subtitle">${escapeHtml(subtitle)}</div>` : ''}
  ${lines || '<p> </p>'}
  <script>window.onload=function(){window.focus();window.print();};<\/script>
</body>
</html>`)
    popup.document.close()
    return true
  } catch (error) {
    if (typeof console !== 'undefined' && console.warn) console.warn('Unable to prepare PDF download.', error)
    if (popup && typeof popup.close === 'function') popup.close()
    return false
  }
}

export function downloadTextDocument(options = {}) {
  const { format } = options
  if (String(format || '').toLowerCase() === 'pdf') {
    return downloadTextPdf(options)
  }
  return downloadTextDocx(options)
}
